import { extend } from 'quasar';
import axios from 'axios';
import { defineStore } from 'pinia';

import { api } from 'src/boot/axios';
import { LOCAL_SITE } from 'src/config/api';
import { getCustomerFontList } from 'src/helpers/editor/customerFonts';
import feedback from 'src/helpers/editor/feedback';
import { type Language } from 'src/models/apiPages';

interface State {
  loading: boolean;
  customer: string;
  fontsLoaded: string[];
  googleFontsPromise: Promise<string[]> | null;
  customerSettings: CustomerSettings | null;
}

export interface CustomerSettings {
  id: string;
  customer: string;
  inactive: boolean;
  deactivatedAt: string | null;
  crmToken: string;
  /** @deprecated Este campo não é mais utilizado. */
  crmUrl: string;
  letsbookUrl: string;
  letsbookApiId: string;
  letsbookLandingPageId: number | null;
  domain: string[];
  lgpdRecipients?: string[];
  emails: string[];
  bodyFont: string;
  headingFont: string;
  globalGtm: string;
  favicon: string;
  headScript: string;
  metatags: string;
  siteName: string;
}

interface CustomerSettingsResponse extends Partial<
  Omit<CustomerSettings, 'id' | 'customer'>
> {
  id: string;
  customer: string;
}

export interface ErrorLog {
  code: string;
  customer?: string;
  url?: string;
  idLets?: string;
  [key: string]: any; // any pois podemos enviar qualquer tipo de dado aqui
}

/**
 * Chave é o ano (ex: '2025').
 * O valor é um array de números com dados por mês, onde o índice
 * representa o mês (0 = jan, 1 = fev, ..., até no máximo 11 = dez).
 * O array pode ter menos de 12 itens, por exemplo, se o ano ainda não terminou.
 */
export interface LeadsMetricsResponse {
  [year: string]: number[];
}

export interface RankingLeadsResponse {
  id: string;
  isPublished: boolean;
  language: Language;
  title: string;
  urlPath: string;
  totalLeads: number;
}

export interface SeoMetricsResponse {
  incompleted: number;
  total: number;
}

export interface RankingSeoResponse {
  id: string;
  seo: number;
  isPublished: boolean;
  language: Language;
  title: string;
  urlPath: string;
}

export const useSettingsStore = defineStore('settings', {
  state: (): State => ({
    loading: false,
    customer: '',
    fontsLoaded: ['Roboto'],
    googleFontsPromise: null,
    customerSettings: null,
  }),
  getters: {
    siteDefaultDomain(state): string {
      return `${state.customer}.siteletsbook.com.br`;
    },
    siteOrigin(state): string {
      if (process.env.ENV_NAME === 'local') return LOCAL_SITE;

      if (state.customerSettings && state.customerSettings.domain.length > 0) {
        return `https://${state.customerSettings.domain[0]}`;
      }

      return `https://${this.siteDefaultDomain}`;
    },
    hasLetsbookIntegration(state): boolean {
      if (!state.customerSettings) return false;

      const { letsbookUrl, letsbookApiId, letsbookLandingPageId } =
        state.customerSettings;

      return Boolean(letsbookUrl && letsbookApiId && letsbookLandingPageId);
    },
    hasCRMIntegration(state): boolean {
      return Boolean(state.customerSettings?.crmToken);
    },
    letsbookDomain(state): string | undefined {
      if (!state.customerSettings) return;

      return state.customerSettings.letsbookUrl.replace(
        /^.*\/\/([\w]+\.letsbook\.com\.br).*$/,
        '$1',
      );
    },
  },
  actions: {
    async loadCustomerSettings(): Promise<CustomerSettings> {
      this.loading = true;

      try {
        const { data: settings } =
          await api.get<CustomerSettingsResponse>('customer');

        const settingsWithDefaults: CustomerSettings = {
          inactive: false,
          deactivatedAt: null,
          crmToken: '',
          crmUrl: '',
          letsbookUrl: '',
          letsbookApiId: '',
          letsbookLandingPageId: null,
          domain: [],
          lgpdRecipients: [],
          emails: [],
          bodyFont: '',
          headingFont: '',
          globalGtm: '',
          favicon: '',
          headScript: '',
          metatags: '',
          siteName: '',
          ...settings,
        };

        this.customerSettings = settingsWithDefaults;

        return settingsWithDefaults;
      } catch (error) {
        feedback.negative();
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async saveCustomerSettings(
      settingsToUpdate: Partial<CustomerSettings>,
    ): Promise<void> {
      this.loading = true;

      try {
        await api.patch('customer', settingsToUpdate);

        const updatedCustomerSettings = extend<CustomerSettings>(
          {},
          this.customerSettings,
          settingsToUpdate,
        );

        this.customerSettings = updatedCustomerSettings;
      } finally {
        this.loading = false;
      }
    },
    async getLeadsMetrics(): Promise<LeadsMetricsResponse> {
      try {
        const { data } = await api.get<LeadsMetricsResponse>(
          'dashboard/metrics/leads',
        );

        return data;
      } catch (error) {
        feedback.negative();
        return {};
      }
    },
    async getRankingLeads(limit = 5): Promise<RankingLeadsResponse[]> {
      try {
        const { data } = await api.get<RankingLeadsResponse[]>(
          `dashboard/metrics/ranking/leads?limit=${limit}`,
        );

        return data;
      } catch (error) {
        feedback.negative();
        return [];
      }
    },
    async getSeoMetrics(): Promise<SeoMetricsResponse> {
      try {
        const { data } = await api.get<SeoMetricsResponse>(
          'dashboard/metrics/seo',
        );

        return data;
      } catch (error) {
        feedback.negative();
        return { incompleted: 0, total: 0 };
      }
    },
    async getRankingSeo(limit = 5): Promise<RankingSeoResponse[]> {
      try {
        const { data } = await api.get<RankingSeoResponse[]>(
          `dashboard/metrics/ranking/seo?limit=${limit}`,
        );

        return data;
      } catch (error) {
        feedback.negative();
        return [];
      }
    },
    getGoogleFonts(): Promise<string[]> {
      if (this.googleFontsPromise) {
        return this.googleFontsPromise;
      }

      interface GoogleApiData {
        items: {
          family: string;
          subsets: string[];
          variants: string[];
        }[];
      }

      // Ref: https://developers.google.com/fonts/docs/developer_api
      const baseUrl = `https://www.googleapis.com/webfonts/v1/webfonts?key=${import.meta.env.VITE_GOOGLE_FONTS_KEY}`;
      const params =
        '&subset=latin&sort=popularity&fields=items.family,items.subsets,items.variants';

      const googleFontsPromise: Promise<string[]> = (async () => {
        const response: { data: GoogleApiData } = await axios(
          `${baseUrl}${params}`,
        );

        const fonts = response.data.items
          .filter((font) => {
            // fonte solicitada por AHI
            if (font.family === 'Quando') return true;

            return (
              font.variants.length > 3 &&
              !font.family.includes('Barcode') &&
              !font.family.includes('Noto Sans')
            );
          })
          .slice(0, 1000) // exibe apenas as 1000 mais populares
          .map((font) => font.family);

        return fonts;
      })();

      this.googleFontsPromise = googleFontsPromise;

      return googleFontsPromise;
    },
    loadFont(fontFamily: string): void {
      if (
        fontFamily &&
        fontFamily !== 'global' &&
        !this.fontsLoaded.includes(fontFamily)
      ) {
        const customerFonts = getCustomerFontList(this.customer);
        const isCustomerFont = customerFonts.includes(fontFamily);

        if (!isCustomerFont) {
          const link = document.createElement('link');
          link.href = `https://fonts.googleapis.com/css2?family=${fontFamily}:wght@200;300;400;500;600;700;800&display=swap`;
          link.rel = 'stylesheet';
          document.head.append(link);
        }

        this.fontsLoaded.push(fontFamily);
      }
    },
    logError(error: ErrorLog): void {
      if (process.env.DEV) return; // o envio de email não funciona sem configuração específica

      try {
        api.post(
          '/errors',
          extend(error, {
            customer: this.customer,
            url: window.location.href,
            idLets: this.customerSettings?.letsbookApiId,
          }),
        );
      } catch (err) {
        console.warn(err);
      }
    },
  },
});
